// Theme = light | dark, applied as data-theme on <html> and mirrored to the
// browser theme-color meta (themeColor.ts). Persisted in Prefs. Both the app
// boot (hydrate) and the reader's Tools menu drive theme through these helpers,
// so neither needs to hold a button reference - the live source of truth is the
// data-theme attribute on the document element.

import { updatePrefs } from "./store.ts";
import { syncThemeColor } from "./themeColor.ts";

export type Theme = "light" | "dark";

export function currentTheme(): Theme {
  return document.documentElement.dataset.theme === "dark" ? "dark" : "light";
}

// How long the "theme-anim" class is held on <html> so the CSS color crossfade
// can play (matches the 0.42s transition in app.css, + a small tail). Only added
// on an EXPLICIT user toggle (animate=true) - boot/hydrate apply the theme with
// animate=false so the first paint never crossfades. CSS suppresses it under
// prefers-reduced-motion.
const THEME_ANIM_MS = 480;
let themeAnimTimer: ReturnType<typeof setTimeout> | null = null;

export function applyTheme(theme: Theme, animate = false): void {
  const root = document.documentElement;
  if (animate && root.dataset.theme !== theme) {
    root.classList.add("theme-anim");
    if (themeAnimTimer) clearTimeout(themeAnimTimer);
    themeAnimTimer = setTimeout(() => root.classList.remove("theme-anim"), THEME_ANIM_MS);
  }
  root.dataset.theme = theme;
  syncThemeColor(theme);
}

export async function persistTheme(theme: Theme): Promise<void> {
  try {
    await updatePrefs({ theme });
  } catch {
    /* ignore persistence failure - theme still applied this session */
  }
}
